import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { Navigate, createBrowserRouter, useParams } from 'react-router'
import { ApiError, api } from './api'
import { AuthProvider, PlatformAdminRoute, ProtectedRoute } from './auth'
import { ErrorState, LoadingState, ResourceScreen, RouteErrorBoundary } from './components/States'

const Dashboard = lazy(() =>
  import('./pages/Dashboard').then((module) => ({ default: module.Dashboard })),
)
const InvitationPage = lazy(() =>
  import('./pages/Invitation').then((module) => ({ default: module.InvitationPage })),
)
const Login = lazy(() => import('./pages/Login').then((module) => ({ default: module.Login })))
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPassword').then((module) => ({ default: module.ResetPasswordPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((module) => ({ default: module.SettingsPage })),
)
const Investigation = lazy(() =>
  import('./pages/Investigation').then((module) => ({ default: module.Investigation })),
)

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState label="Loading view" />}>{children}</Suspense>
}

function AuthRoot() {
  return (
    <AuthProvider>
      <ProtectedRoute />
    </AuthProvider>
  )
}

function PublicRoot({ children }: { children: React.ReactNode }) {
  return <AuthProvider>{children}</AuthProvider>
}

function ProjectLanding() {
  const { projectId = '' } = useParams()
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.project(projectId),
  })
  if (project.isLoading) return <LoadingState label="Opening repository" />
  if (project.error instanceof ApiError && [403, 404].includes(project.error.status))
    return <ResourceScreen kind={project.error.status === 403 ? '403' : '404'} />
  if (project.error)
    return <ErrorState error={project.error} retry={() => void project.refetch()} />
  const run = project.data?.project.current_run
  return run ? (
    <Navigate to={`/repositories/${projectId}/runs/${run.id}/leads`} replace />
  ) : (
    <Navigate to="/" replace />
  )
}

const investigationPaths = [
  'leads',
  'leads/:entityId',
  'routes',
  'routes/:entityId',
  'code',
  'changes',
  'changes/:entityId',
  'notebook',
  'notebook/:entityId',
  'index-report',
]

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <PublicRoot>
        <Page>
          <Login />
        </Page>
      </PublicRoot>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/invite/:token',
    element: (
      <PublicRoot>
        <Page>
          <InvitationPage />
        </Page>
      </PublicRoot>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/reset-password/:token',
    element: (
      <PublicRoot>
        <Page>
          <ResetPasswordPage />
        </Page>
      </PublicRoot>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <AuthRoot />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Page>
            <Dashboard />
          </Page>
        ),
      },
      { path: '/repositories/:projectId', element: <ProjectLanding /> },
      { path: '/repositories/:projectId/runs/:runId', element: <Navigate to="leads" replace /> },
      ...investigationPaths.map((path) => ({
        path: `/repositories/:projectId/runs/:runId/${path}`,
        element: (
          <Page>
            <Investigation />
          </Page>
        ),
      })),
      {
        path: '/settings/:section?',
        element: (
          <Page>
            <SettingsPage />
          </Page>
        ),
      },
      {
        element: <PlatformAdminRoute />,
        children: [{ path: '/admin', element: <Navigate to="/settings/accounts" replace /> }],
      },
      { path: '/403', element: <ResourceScreen kind="403" /> },
      { path: '/404', element: <ResourceScreen kind="404" /> },
      { path: '/archived', element: <ResourceScreen kind="archived" /> },
      { path: '/deleted', element: <ResourceScreen kind="deleted" /> },
      { path: '*', element: <ResourceScreen kind="404" /> },
    ],
  },
])
